import { Link } from 'gatsby';
import React, { Dispatch, FC, SetStateAction, useContext, useRef } from 'react';
import styled, { css } from 'styled-components';
import { UiStateContext } from '../../context/UiStateContext';
import { Kfh_PropertyTypeInterface } from '../../types/graphqlTypes';
import { createMarkup, currency } from '../../utils';
import { ArrangeAViewingContactPropertyProps } from '../../utils/getPropertyContactFields';
import { Button } from '../Button';
import { Gallery } from '../Gallery';
import { Alert } from '../icon/icons/Alert';
import { Save } from '../icon/icons/Save';
import { Saved } from '../icon/icons/Saved';
import { Trash } from '../icon/icons/Trash';
import { IconButton } from '../IconButton';
import { Typography } from '../Typography';
import PropertyMeta from './PropertyMeta';
import PropertyPriceAndStatus from './PropertyPriceAndStatus';
import NoPhoto from '../../images/no-photo.png';
import { Img } from '../Img';
import { useSaveProperty } from '../../hooks/useSaveProperty';
import { UserContext } from '../../context/UserContext';
import { Loader } from '../Loader';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import { getUrlParts } from '../../utils/getUrlParts';
import { ActivePropertyContext } from '../../context/ActivePropertyContext';
import { getCleanPropertyDetails } from '../../utils/getCleanPropertyDetails';

interface PropertyCardProps {
  property: Kfh_PropertyTypeInterface;
  as?: 'div' | 'li';
  vertical?: boolean;
  disableGallerySwipeOnMobile?: boolean;
  onWhiteBg?: boolean;
  confirmDelete?: boolean;
  unSaveable?: boolean;
  hideActions?: boolean;
  prioritiseViewing?: boolean;
  onClick?: () => void;
  clickSection?: string;
  displayMobileCta?: boolean;
  hidePrice?: boolean;
  position?: number;
}

interface StyledPropertyCardProps {
  vertical?: boolean;
  $onWhiteBg?: boolean;
  singleImage?: boolean;
  displayMobileCta?: boolean;
}

const StyledPropertyCard = styled.div<StyledPropertyCardProps>`
  ${({ theme: { space, borders, colors, media, global }, vertical, singleImage }) => css`
    background-color: ${colors.white.default};
    border-radius: ${global.borderRadius.default};
    overflow: hidden;
    isolation: isolate;
    transform: translateZ(0);

    .gatsby-image-wrapper {
      max-width: 100%; // cls fix
      height: auto; // cls fix
      width: 100%;
      background-color: ${colors.grey1.default};
    }
    .property-card-image {
      height: 100%;
      width: 100%;
      aspect-ratio: 4/3;
      @media (min-width: ${media.lg}) {
        aspect-ratio: 191/180;
      }

      > div {
        height: 100%;
        width: 100%;
        background-color: ${colors.grey1.default};
      }
      img {
        object-fit: cover;
        height: 100%;
        width: 100%;
      }
    }

    ${vertical &&
    css`
      display: flex;
      flex-direction: column;
    `};

    ${!vertical &&
    css`
      @media (min-width: ${media.lg}) {
        display: grid;
        grid-template-columns: 1fr 470px;
      }

      @media (min-width: ${media.xl}) {
        display: grid;
        grid-template-columns: ${singleImage ? '360px 1fr' : '576px 1fr'};
      }
    `};
  `};
`;

const StyledBody = styled.div<StyledPropertyCardProps>`
  ${({ theme: { space, media, maxMedia }, vertical, $onWhiteBg, displayMobileCta }) => css`
    padding: ${space.md};
    position: relative;

    @media (max-width: ${maxMedia.md}) {
      ${displayMobileCta &&
      css`
        display: flex;
        flex-direction: column;
      `};
    }

    ${vertical &&
    css`
      flex-grow: 1;
    `};

    ${$onWhiteBg &&
    css`
      padding-right: 0;
      padding-left: 0;
    `};

    @media (min-width: ${media.md}) {
      padding: ${vertical ? `${space.md}` : space.lg};
      display: flex;
      flex-direction: column;

      ${$onWhiteBg &&
      css`
        padding-right: 0;
        padding-left: 0;
      `};
    }
  `};
`;

const StyledBodyInner = styled.div<StyledPropertyCardProps>`
  ${({ theme: { space, media }, vertical }) => css`
    ${vertical &&
    css`
      flex-grow: 1;
    `};
  `};
`;

const StyledAddress = styled(Typography)`
  ${({ theme: { space, media } }) => css`
    margin-top: ${space.sm};

    @media (min-width: ${media.md}) {
      margin-top: ${space.md};
    }
  `};
`;

const StyledDescription = styled(Typography)`
  ${({ theme: { space, maxMedia } }) => css`
    margin-top: ${space.md};
    margin-bottom: ${space.md};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: ${maxMedia.md}) {
      display: none;
    }
  `};
`;

const StyledCommercialDescription = styled(Typography)`
  ${({ theme: { space, maxMedia } }) => css`
    margin-top: ${space.md};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-bottom: ${space.md};

    @media (max-width: ${maxMedia.md}) {
      display: none;
    }
  `};
`;

const StyledActions = styled.div<StyledPropertyCardProps>`
  ${({ theme: { space, media }, vertical, displayMobileCta }) => css`
    display: ${displayMobileCta ? 'flex' : 'none'};

    ${displayMobileCta &&
    css`
      flex-direction: column;
      gap: ${space.sm};
      margin-top: ${vertical ? space.md : 'auto'};
      flex-direction: column-reverse;
    `};

    @media (min-width: ${media.lg}) {
      display: flex;
      gap: ${space.sm};
      margin-top: ${vertical ? space.md : 'auto'};

      ${vertical &&
      css`
        flex-direction: column-reverse;
      `};
    }
  `};
`;

const StyledSavedPropertyActions = styled.div<StyledPropertyCardProps>`
  ${({ theme: { space, media, layers }, vertical, $onWhiteBg }) => css`
    position: absolute;
    top: 24px;
    right: ${space.md};
    z-index: ${layers.propertyCardSaveButton};
    display: flex;
    align-items: center;
    gap: ${space.md};

    ${$onWhiteBg &&
    css`
      right: 0;
    `};

    @media (min-width: ${media.lg}) {
      top: ${vertical ? '24px' : space.lg};
      right: ${vertical ? space.md : space.lg};

      ${$onWhiteBg &&
      css`
        right: 0;
      `};
    }
  `};
`;

const StyledSavedPropertyItem = styled.span`
  ${({ theme: { space, media, maxMedia } }) => css`
    display: flex;
    align-items: center;

    @media (min-width: ${media.md}) {
      gap: ${space.sm};
    }
    @media (max-width: ${maxMedia.md}) {
      flex-direction: column;
      gap: 0;
      margin-top: -${space.sm};
    }
  `};
`;
const StyledSavedPropertyLabel = styled(Typography)`
  ${({ theme: { space, media, maxMedia } }) => css`
    font-weight: bold;
    @media (min-width: ${media.md}) {
      margin-top: -${space.xxs};
    }
    @media (max-width: ${maxMedia.md}) {
      margin-top: -${space.xs};
    }
  `};
`;

const StyledAddressLink = styled(Link)<StyledPropertyCardProps>`
  ${({ theme: { maxMedia }, displayMobileCta }) => css`
    text-decoration: none;

    ${!displayMobileCta &&
    css`
      @media (max-width: ${maxMedia.lg}) {
        &:after {
          content: '';
          position: absolute;
          z-index: 1;
          top: 0;
          left: 0;
          right: 0;
          height: 100%;
          background: rgba(255, 255, 255, 0);
        }
      }
    `};
  `};
`;

const StyledPropertyPrice = styled.div<StyledPropertyCardProps>`
  ${({ theme: { space, maxMedia }, vertical }) => css`
    ${vertical &&
    css`
      flex-grow: 1;
    `};
  `};
`;

export const PropertyCard: FC<PropertyCardProps> = ({
  property,
  as,
  vertical,
  disableGallerySwipeOnMobile,
  onWhiteBg,
  confirmDelete,
  unSaveable,
  hideActions,
  hidePrice,
  displayMobileCta,
  prioritiseViewing,
  clickSection,
  position,
  onClick,
}) => {
  const { dataLayerPush } = useDataLayerPush();
  const { setActiveBlade, setActiveBladeTriggerEl } = useContext(UiStateContext);
  const { setActiveProperty } = useContext(ActivePropertyContext);
  const arrangeAViewingTriggerEl = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const { user, setRegisterData } = useContext(UserContext);
  const {
    isSaved,
    alertsEnabled,
    handleSaveProperty,
    handleUpdateProperty,
    savingLoading,
    deleteLoading,
    updateLoading,
    loading,
  } = useSaveProperty(
    property.uri,
    property.seo
      ? property.seo.h1
      : `${property.address?.streetname}, ${property.address?.town} ${property.address?.outwardCode}`,
    property
  );

  // GA property click
  const propertyClick = {
    event: 'propertyClick',
    clickText: 'property details',
    clickBladeID: false,
    position,
    propertyClickDetails: getCleanPropertyDetails(property),
    clickSection,
    ...getUrlParts(property.relativeUrl),
  };

  // Handle Blades

  const handleOpenBlade = (bladeId, triggerEl) => {
    if (setActiveProperty) {
      setActiveProperty(property);
      setActiveBlade(bladeId);
      setActiveBladeTriggerEl(triggerEl);
      // dataLayerPush({
      //   clickType: 'cta arrange a viewing',
      //   ...propertyClick,
      // });
    }
  };

  // Link state (passed through to property details for back link)

  const linkState = { prevPageType: 'property-listing' };

  // Handle saving property

  const handleClickSaveProperty = () => {
    if (user?.id) {
      handleSaveProperty();
    } else if (setActiveProperty) {
      setRegisterData({
        action: 'saveProperty',
        data: {
          propertyUri: property.uri,
        },
      });
      setActiveProperty(property);
      setActiveBlade('savePropertyLoginOrRegister');
    }
  };

  const handleConfirmDeleteProperty = () => {
    if (setActiveProperty) {
      setActiveProperty(property);
      setActiveBlade('deleteSavedProperty');
    }
  };

  const handleUpdateSavedPropertyAlert = () => {
    handleUpdateProperty();
  };

  return (
    <>
      <StyledPropertyCard as={as} vertical={vertical} singleImage={property.commercial} onClick={onClick}>
        {/* Gallery */}
        <Gallery
          property={property}
          propertyCard
          vertical={vertical || property.commercial}
          disableGallerySwipeOnMobile={disableGallerySwipeOnMobile}
        >
          {property.images && property.images.photographs && property.images.photographs.length >= 1 ? (
            property.images.photographs.map((image, index) => (
              <Link
                className="property-card-image"
                to={property.relativeUrl}
                state={linkState}
                key={`card-img-${index}`}
                onClick={() =>
                  dataLayerPush({
                    clickType: 'gallery image',
                    ...propertyClick,
                  })
                }
              >
                <Img
                  src={image}
                  alt={image.caption}
                  loading={index > 2 ? 'lazy' : undefined}
                  size="430x320"
                  options={{
                    width: 382,
                    height: 360,
                    layout: 'constrained',
                    cropFocus: 'center',
                    placeholder: 'none',
                    quality: 80,
                    backgroundColor: '#F5F5F5',
                  }}
                />
              </Link>
            ))
          ) : (
            <Link
              to={property.relativeUrl}
              state={linkState}
              onClick={() =>
                dataLayerPush({
                  clickType: 'gallery image',
                  ...propertyClick,
                })
              }
            >
              <Img src={NoPhoto} alt="" />
            </Link>
          )}
        </Gallery>

        {/* Body */}

        <StyledBody vertical={vertical} $onWhiteBg={onWhiteBg} displayMobileCta={displayMobileCta}>
          <StyledBodyInner vertical={vertical}>
            {/* Property Price and status */}
            {!hidePrice && (
              <StyledPropertyPrice vertical={vertical}>
                <PropertyPriceAndStatus property={property} propertyCard />
              </StyledPropertyPrice>
            )}

            {/* Address */}

            <StyledAddress component="h2" variant="titleSmall">
              <StyledAddressLink
                to={property.relativeUrl}
                state={linkState}
                onClick={() =>
                  dataLayerPush({
                    clickType: 'address title',
                    ...propertyClick,
                  })
                }
                displayMobileCta={displayMobileCta}
              >
                {property.address?.streetname}, {property.address?.town} {property.address?.outwardCode}
              </StyledAddressLink>
            </StyledAddress>

            {/* Meta */}

            <PropertyMeta property={property} propertyCard />

            {/* Description */}

            {property.summary && !vertical ? (
              <StyledDescription component="p" variant="bodySmall">
                {createMarkup(property.summary)}
              </StyledDescription>
            ) : (
              ''
            )}

            {property.commercial && property.description && !vertical ? (
              <StyledCommercialDescription component="p" variant="bodySmall">
                {createMarkup(property.description)}
              </StyledCommercialDescription>
            ) : (
              ''
            )}
          </StyledBodyInner>

          {/* Actions */}

          {!hideActions && (
            <StyledActions vertical={vertical} displayMobileCta={displayMobileCta}>
              <Button
                url={property.relativeUrl}
                variant={prioritiseViewing ? 'outline' : 'solid'}
                state={linkState}
                disableClickTracking
                onClick={() =>
                  dataLayerPush({
                    clickType: 'cta button',
                    ...propertyClick,
                  })
                }
              >
                Property details
              </Button>
              {property.available && (
                <Button
                  type="button"
                  variant={prioritiseViewing ? 'solid' : 'outline'}
                  onClick={() => handleOpenBlade('propertyArrangeAViewing', arrangeAViewingTriggerEl)}
                  ref={arrangeAViewingTriggerEl}
                  eventClickBladeId="propertyArrangeAViewing"
                  label="Arrange a viewing"
                />
              )}
            </StyledActions>
          )}

          {/* Save property */}
          {!unSaveable && (
            <StyledSavedPropertyActions vertical={vertical} $onWhiteBg={onWhiteBg}>
              {confirmDelete && (
                <>
                  <IconButton
                    type="button"
                    onClick={handleUpdateSavedPropertyAlert}
                    a11yTitle={alertsEnabled ? 'Disable alert' : 'Enable alert'}
                    strikeThrough={!alertsEnabled && !updateLoading}
                    disabled={loading}
                  >
                    {updateLoading ? <Loader size="small" /> : <Alert />}
                  </IconButton>
                  |
                </>
              )}

              <IconButton
                a11yTitle="Save property"
                onClick={() => {
                  if (confirmDelete) {
                    handleConfirmDeleteProperty();
                  } else {
                    handleClickSaveProperty();
                  }
                }}
              >
                {confirmDelete ? (
                  <>
                    <Trash />
                  </>
                ) : (
                  <>
                    {savingLoading || deleteLoading ? (
                      <Loader size="medium" />
                    ) : (
                      <>
                        {isSaved ? (
                          <>
                            <StyledSavedPropertyItem>
                              <Saved size="medium" />
                              <StyledSavedPropertyLabel component="span" variant="bodySmall">
                                Saved
                              </StyledSavedPropertyLabel>
                            </StyledSavedPropertyItem>
                          </>
                        ) : (
                          <>
                            <StyledSavedPropertyItem>
                              <Save size="medium" />
                              <StyledSavedPropertyLabel component="span" variant="bodySmall">
                                Save
                              </StyledSavedPropertyLabel>
                            </StyledSavedPropertyItem>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}

                <span id={`confettiReward-${property.uri}`} />
              </IconButton>
            </StyledSavedPropertyActions>
          )}
        </StyledBody>
      </StyledPropertyCard>
    </>
  );
};
